// The shared gray ramp (tokens.md §1.1): register and pronto, identical in light and dark.
export const gray = {
  gray00: '#ffffff',
  gray25: '#fafafa',
  gray50: '#ededed',
  gray75: '#dedfdf',
  gray100: '#cccdcb',
  gray200: '#b3b4b3',
  gray400: '#6f7070',
  gray500: '#5d5d5d',
  gray550: '#36373a',
  gray600: '#343537',
  gray650: '#313235',
  gray675: '#2a2b2e',
  gray700: '#262629',
  gray750: '#232427',
  gray800: '#212224',
  gray900: '#1c1d20',
  gray950: '#101114',
} as const;

export type GrayToken = keyof typeof gray;

/** Product / category palette (tokens.md §1.2, register `src/library/color.ts`): the stripe colour names a category carries. */
export const productPalette = {
  blue: '#0579f7',
  'deep-purple': '#512da8',
  pink: '#e6457a',
  green: '#04c635',
  indigo: '#4f62cc',
  maroon: '#9a0a0a',
  red: '#f4261b',
  cyan: '#58bae8',
  gold: '#dcb332',
  orange: '#ff9500',
  'orange-deep': '#ff7031',
  teal: '#08bad0',
  brown: '#7e6250',
  purple: '#7A62E0',
  'dark-green': '#0A7602',
  grey: '#9da0a0',
  gray: '#9da0a0',
  'light-green': '#62ce96',
  'light-blue': '#0579f7',
  'dark-blue': '#005ca1',
  salmon: '#ef6e7e',
  'very-dark': '#1c232e',
  // Legacy `$grayAlternate`: a location colour legacy accepts but its picker does not offer.
  'gray-alternate': '#859da4',
  'lime-green': '#2dbf52',
  'cash-green': '#4caf50',
  yellow: '#F0D064',
} as const;

export type ProductColor = keyof typeof productPalette;

/**
 * The back office's colour for each name (legacy back office `css/_colors.scss` under Quasar's palette, measured on every
 * name's `bg-*` and `text-*` class, 2026-10-23): the register's, except six. Quasar's own `!important` classes win for
 * deep-purple, light-green and light-blue, and legacy's back-office variables differ from the register's for orange,
 * purple and teal. The stored value stays the name (or the register's hex); each surface maps it to its own hex.
 */
export const backofficePalette: Readonly<Record<ProductColor, string>> = {
  ...productPalette,
  'deep-purple': '#673ab7',
  'light-green': '#8bc34a',
  'light-blue': '#03a9f4',
  orange: '#f79206',
  purple: '#ad48e0',
  teal: '#26a69a',
};

/**
 * The back office's hex for a stored colour: a palette name, or a register hex as the catalog stores it (the first name
 * with that hex, so the register's shared #0579f7 reads as blue). Anything else is drawn as stored.
 */
export function backofficeColor(stored: string): string {
  const named = (backofficePalette as Readonly<Record<string, string>>)[stored];
  if (named !== undefined) return named;
  const hex = stored.toLowerCase();
  const name = (Object.keys(productPalette) as ProductColor[]).find((n) => productPalette[n].toLowerCase() === hex);
  return name === undefined ? stored : backofficePalette[name];
}
